/** biome-ignore-all lint/suspicious/noConsole: This is the logger. It logs. */
import { LogLevel, LogMessage, LogLevelPriority } from './logger.type';

const LOG_GROUP_FORMAT = [
  'color: white',
  'background: #63599A',
  'padding: 4px',
  'font-weight: bold',
  'border-radius: 7px 0 0 7px'
].join(';');

const LOG_BUBBLE_FORMAT = [
  'color: black',
  'background: #B6AFDB',
  'padding: 4px 8px 4px 4px',
  'font-weight: bold',
  'border-radius: 0 7px 7px 0'
].join(';');

const pad = (str: string, length: number, char = ' '): string => {
  return str.padEnd(length, char).substring(0, length);
};

interface LoggerConfig {
  logLevel: LogLevel;
  shouldTrace: boolean;
  onLog?: (message: LogMessage, level: LogLevel) => void;
}

class Logger {
  private LoggerConfig: LoggerConfig | null = null;

  private ensureLoggerIsConfigured() {
    if (this.LoggerConfig === null)
      throw new Error(
        'Logger used before configuration. Call setConfig() on the Logger instance before logging.'
      );
  }

  private constructLogMessage(log: LogMessage): string {
    const timeStamp = new Date().toISOString();
    if (!log.correlationId) return `[${timeStamp}] - ${log.message}`;
    return `[${timeStamp}][Correlation: ${log.correlationId}] - ${log.message}`;
  }

  private log(title: string, logMessage: LogMessage, logFn: (msg: string) => void) {
    const message = this.constructLogMessage(logMessage);
    console.groupCollapsed(
      `%c${pad(title, 20)}▼%c${logMessage.message}`,
      LOG_GROUP_FORMAT,
      LOG_BUBBLE_FORMAT
    );
    logFn(message);
    if (logMessage.object) console.log(logMessage.object);
    if (this.LoggerConfig?.shouldTrace && logMessage.includeTrace) {
      console.trace('Stack trace:');
    }
    console.groupEnd();
  }

  private shouldLogAt(level: LogLevel): boolean {
    const configLogLevel = this.LoggerConfig?.logLevel ?? LogLevel.NONE;
    try {
      const configLogLevelPriority = LogLevelPriority[configLogLevel];
      const currentLogLevelPriority = LogLevelPriority[level];
      const shouldLog = currentLogLevelPriority <= configLogLevelPriority;
      return shouldLog;
    } catch (_error) {
      console.error('Log level is not supported: ', configLogLevel);
      return false;
    }
  }

  public setConfig(config: LoggerConfig) {
    this.LoggerConfig = config;
  }

  public info(logMessage: LogMessage) {
    this.ensureLoggerIsConfigured();
    if (!this.shouldLogAt(LogLevel.INFO)) return;
    this.LoggerConfig?.onLog?.(logMessage, LogLevel.INFO);
    this.log('Information log', logMessage, console.info);
  }

  public error(logMessage: LogMessage) {
    this.ensureLoggerIsConfigured();
    if (!this.shouldLogAt(LogLevel.ERROR)) return;
    this.LoggerConfig?.onLog?.(logMessage, LogLevel.ERROR);
    logMessage.includeTrace ??= true;
    this.log('Error log', logMessage, console.error);
  }

  public warning(logMessage: LogMessage) {
    this.ensureLoggerIsConfigured();
    if (!this.shouldLogAt(LogLevel.WARN)) return;
    this.LoggerConfig?.onLog?.(logMessage, LogLevel.WARN);
    this.log('Warning log', logMessage, console.warn);
  }

  public debug(logMessage: LogMessage) {
    this.ensureLoggerIsConfigured();
    if (!this.shouldLogAt(LogLevel.DEBUG)) return;
    this.LoggerConfig?.onLog?.(logMessage, LogLevel.DEBUG);
    logMessage.includeTrace ??= true;
    this.log('Debug log', logMessage, console.debug);
  }
}

const logger = new Logger();
export default logger;
